import React from "react";
import { FiWind, FiThermometer, FiDroplet, FiAlertTriangle, FiActivity, FiCpu } from "react-icons/fi";
import { AqiGauge, AqiBadge, KpiCard, aqiMeta, co2Color, pm25Color, tvocColor } from "./aqmHelpers";

const StatsBar = ({ label, value, max, color }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
      <span style={{ color: "#6b7280" }}>{label}</span>
      <span style={{ color, fontWeight: 600 }}>{value}</span>
    </div>
    <div style={{ background: "#f3f4f6", borderRadius: 4, height: 6, overflow: "hidden" }}>
      <div style={{ width: `${Math.min(100, (parseFloat(value) / max) * 100)}%`, background: color, height: "100%", borderRadius: 4 }} />
    </div>
  </div>
);

const AqmIndoorOverviewPanel = ({ d }) => {
  const { summary, floors, weeklyAqi, alerts, aiInsights } = d;
  const m = aqiMeta(summary.overallAqi);
  const maxWeekly = Math.max(...weeklyAqi.map(w => w.aqi));

  return (
    <div className="aqm-overview">

      {/* ── Hero: AQI + Site ── */}
      <div className="aqm-ov-hero">
        <div className="aqm-aqi-hero-card">
          <AqiGauge aqi={summary.overallAqi} size={120} />
          <div>
            <div className="aqm-aqi-hero-label">Overall Indoor AQI</div>
            <div className="aqm-aqi-hero-val" style={{ color: m.color }}>{summary.overallAqi}</div>
            <AqiBadge aqi={summary.overallAqi} />
            <div className="aqm-aqi-hero-site">{d.site.name}</div>
            <div className="aqm-aqi-hero-loc">{d.site.location}</div>
            <div className="aqm-aqi-hero-meta">{d.site.floors} Floors · {d.site.totalRooms} Rooms · {d.site.onlineDevices}/{d.site.totalDevices} Devices Online</div>
          </div>
        </div>

        {/* Alert summary */}
        <div className="aqm-ov-alert-card">
          <div className="aqm-section-title">Active Alerts</div>
          <div className="aqm-ov-alert-nums">
            <div className="aqm-alert-num" style={{ borderTopColor: "#ef4444" }}>
              <div className="aqm-an-val" style={{ color: "#ef4444" }}>{summary.criticalAlerts}</div>
              <div className="aqm-an-lbl">Critical</div>
            </div>
            <div className="aqm-alert-num" style={{ borderTopColor: "#f59e0b" }}>
              <div className="aqm-an-val" style={{ color: "#f59e0b" }}>{summary.activeAlerts - summary.criticalAlerts}</div>
              <div className="aqm-an-lbl">Warning</div>
            </div>
          </div>
          <div className="aqm-section-title" style={{ marginTop: 14 }}>AI Key Insights</div>
          {aiInsights.slice(0,3).map((ins, i) => (
            <div key={i} className="aqm-ov-insight-row">
              <span style={{ fontSize: 16 }}>{ins.icon}</span>
              <span style={{ fontSize: 12, color: "#374151", flex: 1 }}>{ins.title}</span>
              <span className="aqm-badge" style={{ background: ins.priority === "High" ? "#fee2e2" : ins.priority === "Medium" ? "#fef3c7" : "#dbeafe", color: ins.priority === "High" ? "#ef4444" : ins.priority === "Medium" ? "#f59e0b" : "#3b82f6", fontSize: 10 }}>{ins.priority}</span>
            </div>
          ))}
        </div>

        {/* Pollutant bars */}
        <div className="aqm-ov-pollutant-card">
          <div className="aqm-section-title">Key Pollutant Levels</div>
          <StatsBar label="PM2.5"        value={`${summary.pm25} µg/m³`} max={120}  color={pm25Color(summary.pm25)} />
          <StatsBar label="PM10"         value={`${summary.pm10} µg/m³`} max={200}  color={pm25Color(summary.pm10 / 2)} />
          <StatsBar label="CO₂"          value={`${summary.co2} ppm`}    max={2000}  color={co2Color(summary.co2)} />
          <StatsBar label="TVOC"         value={`${summary.tvoc} mg/m³`} max={1.5}   color={tvocColor(summary.tvoc)} />
          <StatsBar label="Temperature"  value={`${summary.temperature}°C`} max={40} color="#f97316" />
          <StatsBar label="Humidity"     value={`${summary.humidity}%`}  max={100}   color="#3b82f6" />
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="aqm-kpi-grid">
        <KpiCard label="AQI"           value={summary.overallAqi}          color={m.color}    icon={FiActivity}     sub={m.label} />
        <KpiCard label="PM2.5"         value={summary.pm25}   unit="µg/m³" color={pm25Color(summary.pm25)} icon={FiWind} sub="Standard: < 60 µg/m³" />
        <KpiCard label="PM10"          value={summary.pm10}   unit="µg/m³" color={pm25Color(summary.pm10 / 2)} icon={FiWind} sub="Standard: < 100 µg/m³" />
        <KpiCard label="CO₂"           value={summary.co2}    unit="ppm"   color={co2Color(summary.co2)} icon={FiWind} sub="Optimal: < 800 ppm" alert={summary.co2 > 1000 ? "High — Increase ventilation" : null} />
        <KpiCard label="TVOC"          value={summary.tvoc}   unit="mg/m³" color={tvocColor(summary.tvoc)} icon={FiCpu} sub="Acceptable: < 0.3 mg/m³" />
        <KpiCard label="Temperature"   value={summary.temperature} unit="°C" color="#f97316" icon={FiThermometer} sub="Comfort: 22–26°C" />
        <KpiCard label="Humidity"      value={summary.humidity} unit="%"    color="#3b82f6" icon={FiDroplet} sub="Comfort: 40–60%" />
        <KpiCard label="Active Alerts" value={summary.activeAlerts}          color="#ef4444" icon={FiAlertTriangle} alert={summary.criticalAlerts > 0 ? `${summary.criticalAlerts} critical` : null} />
      </div>

      {/* ── Floor AQI Summary ── */}
      <div className="aqm-ov-bottom">
        <div className="aqm-ov-floor-card">
          <div className="aqm-section-title">Floor-Level AQI Summary</div>
          {floors.map(fl => {
            const fm = aqiMeta(fl.overallAqi);
            return (
              <div key={fl.id} className="aqm-floor-row">
                <div className="aqm-floor-id" style={{ background: fm.color + "22", color: fm.color }}>{fl.label}</div>
                <div className="aqm-floor-name">{fl.name}</div>
                <div style={{ flex: 1, padding: "0 10px" }}>
                  <div style={{ background: "#f3f4f6", borderRadius: 4, height: 8, overflow: "hidden" }}>
                    <div style={{ width: `${Math.min(100, (fl.overallAqi / 200) * 100)}%`, background: fm.color, height: "100%", borderRadius: 4 }} />
                  </div>
                </div>
                <div style={{ color: fm.color, fontWeight: 700, fontSize: 14, minWidth: 36 }}>{fl.overallAqi}</div>
                <span className="aqm-badge" style={{ background: fm.bg, color: fm.text, fontSize: 10 }}>{fm.label}</span>
              </div>
            );
          })}
        </div>

        <div className="aqm-ov-weekly-card">
          <div className="aqm-section-title">Weekly Indoor AQI Trend</div>
          <div className="aqm-bar-chart" style={{ height: 160 }}>
            {weeklyAqi.map(day => {
              const dm = aqiMeta(day.aqi);
              return (
                <div key={day.day} className="aqm-bar-col">
                  <div className="aqm-bar-val" style={{ color: dm.color }}>{day.aqi}</div>
                  <div className="aqm-bar-wrap">
                    <div className="aqm-bar-fill" style={{ height: `${(day.aqi / maxWeekly) * 100}%`, background: dm.color }} />
                  </div>
                  <div className="aqm-bar-lbl">{day.day}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AqmIndoorOverviewPanel;
